import React from "react";
import { Text, plainText } from "../../atoms/text/text";
import type { LineupColumn } from "./channel-lineup.types";
import { isRenderable } from "./is-renderable.shared";
import { ACCENT_STEPS, PackageTile } from "./package-tile";
import { StickyHeader } from "./sticky-header.client";
import styles from "./grid-header.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The Signature packages grid header — GH1-GH9. A left cell carrying the channels wording and the
 * Sort by control, then one tile per authored package, pinned to the top of the viewport while the
 * grid scrolls under it.
 */

export interface GridHeaderProps {
  /** Resolver-fed (camelCase). GH1 — one tile per entry, **in the order given**. */
  columns: LineupColumn[];
  /** GH1's left-cell wording. Absent, the cell carries no wording. */
  channels_label?: string;
  /** GH1's wording beside the sort control. Absent, none is drawn. */
  sort_label?: string;
  /**
   * Root state (camelCase). GH5 — "while a search is active, the Sort by label and the radio
   * buttons are hidden." Both come out of the DOM rather than being visually hidden.
   */
  searchActive?: boolean;
  /**
   * GH4's radio group, built by the caller — the organism root owns the sort value and the
   * callback, and a callback cannot be passed into a Server Component.
   */
  sortSlot?: React.ReactNode;
  /** GH7 — the MyFree DIRECTV banner, which "shows under the package tiles
   *  and stays pinned with the header". Built elsewhere; this places it. */
  bannerSlot?: React.ReactNode;
  /** GH6's shadow condition — "if the MyFree DIRECTV banner is present". */
  hasBanner?: boolean;
  /** Analytics position written on every tile's Shop link. */
  linkPosition?: string;
  className?: string;
}

function GridHeaderImpl({
  columns,
  channels_label: channelsLabel,
  sort_label: sortLabel,
  searchActive = false,
  sortSlot,
  bannerSlot,
  hasBanner,
  linkPosition,
  className
}: GridHeaderProps) {
  const channels = plainText((channelsLabel ?? "").trim());
  const sort = plainText((sortLabel ?? "").trim());
  /* `false` as well as nullish: a `ReactNode` includes `boolean`, and `{showBanner && <Banner />}`
     hands this component `false`. Treating that as present paints GH6's shadow over nothing. */
  const bannerPresent = hasBanner ?? isRenderable(bannerSlot);
  const showSort = !searchActive && isRenderable(sortSlot);

  /* `repeat(0, …)` is invalid CSS and takes the whole declaration with it, leaving a single
     undivided track, so the floor is 1 even with nothing to put in it. */
  const trackCount = Math.max(columns.length, 1);

  /* R1-04.5 — the authored package list decides which packages get a column and in what order, and
     both of its failure modes are silent on the page: a list that resolved to nothing draws a
     header with no tiles rather than an error, and a list longer than the frame covers draws
     columns the design has no glow step for. */
  if (process.env.NODE_ENV !== "production") {
    if (columns.length === 0) {
      console.warn(
        "[GridHeader] no packages resolved, so the grid header renders no " +
          "columns. Check the blok's authored `packages` list — a " +
          "package_id the feed does not carry is dropped upstream."
      );
    } else if (columns.length > ACCENT_STEPS) {
      console.warn(
        `[GridHeader] ${columns.length} package columns were authored and ` +
          `Figma publishes ${ACCENT_STEPS} glow steps, so column ` +
          `${ACCENT_STEPS + 1} onward repeats the last one.`
      );
    }
  }

  return (
    <StickyHeader
      className={className}
      hasBanner={bannerPresent}
    >
      {/* `--cl-columns` is read by the stylesheet's track count. */}
      <div
        className={styles.row}
        style={{ "--cl-columns": trackCount } as React.CSSProperties}
      >
        {/* `!showSort` is the cell's own layout condition, not a restatement of `searchActive`: the cell is short exactly when the sort block does not render, which includes a caller that supplied no `sortSlot`. */}
        <div
          className={[styles.controls, showSort ? null : styles.controlsNoSort]
            .filter(Boolean)
            .join(" ")}
        >
          {/* Subhead 2 medium at tablet and desktop; the stylesheet sets
              mobile's Body 2 and tablet's 24px. */}
          {channels !== "" && (
            <Text
              as="span"
              className={styles.channelsLabel}
              variant="subhead-2"
              weight="medium"
            >
              {channels}
            </Text>
          )}
          {showSort && (
            <div className={styles.sort}>
              {/* GH1 draws this wording in the left cell, outside the radio group's own fieldset — so the group still carries its own accessible name (SortRadios' `ariaLabel`) rather than depending on a label it does not contain. */}
              {sort !== "" && (
                <Text as="span" className={styles.sortLabel} variant="body-3">
                  {sort}
                </Text>
              )}
              {sortSlot}
            </div>
          )}
        </div>

        {/* Deliberately not a <ul>. A list wrapper would have to carry `display: contents` to let its items join this grid, and that is known to drop list semantics in some browsers — an invented, unreliable announcement in place of none. */}
        {columns.map((column, index) => (
          <PackageTile
            countText={column.countText}
            ctaHref={column.ctaHref}
            ctaAriaLabel={column.ctaAriaLabel}
            ctaLabel={column.ctaLabel}
            /* AN3 — the column position the Shop click reports, starting at 0. The map index is
               that position by construction: nothing in this component drops, filters or re-sorts
               `columns` (see its own doc), so the array order is the drawn order. */
            index={index}
            key={column.billingProductCode}
            label={column.label}
            linkPosition={linkPosition}
          />
        ))}
      </div>

      {bannerSlot}
    </StickyHeader>
  );
}
export const GridHeader = withSafeRender(GridHeaderImpl, "GridHeader");

export default GridHeader;
